/**
 * React Hooks for Package Management
 */

import { useState, useEffect } from 'react';
import { packageAPI } from '@/src/lib/api/admin-packages';
import type { CreditPackage, PackageFormData } from '@/src/types/admin/package';
import { toast } from 'sonner';
import { isProtectedPackage } from '@/src/constants/admin/packages';

/**
 * Hook to fetch all packages
 */
export function usePackages(includeInactive = false) {
  const [packages, setPackages] = useState<CreditPackage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchPackages = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await packageAPI.getAll(includeInactive);
      setPackages(data.packages);
    } catch (err: any) {
      const message = err.response?.data?.error || 'Failed to fetch packages';
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPackages();
  }, [includeInactive]);

  return {
    packages,
    loading,
    error,
    refetch: fetchPackages,
  };
}

/**
 * Hook to fetch single package
 */
export function usePackage(id: string | undefined) {
  const [package_data, setPackageData] = useState<CreditPackage | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchPackage = async () => {
    if (!id) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const data = await packageAPI.getById(id);
      setPackageData(data);
    } catch (err: any) {
      const message = err.response?.data?.error || 'Failed to fetch package';
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPackage();
  }, [id]);

  return {
    package: package_data,
    loading,
    error,
    refetch: fetchPackage,
  };
}

/**
 * Hook for package CRUD operations
 */
export function usePackageMutations() {
  const [loading, setLoading] = useState(false);

  const createPackage = async (data: PackageFormData) => {
    try {
      setLoading(true);
      const result = await packageAPI.create(data);
      toast.success('สร้าง package สำเร็จ');
      return result;
    } catch (err: any) {
      const message = err.response?.data?.error || 'Failed to create package';
      toast.error(message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const updatePackage = async (id: string, data: Partial<PackageFormData>) => {
    try {
      setLoading(true);
      const result = await packageAPI.update(id, data);
      toast.success('อัพเดต package สำเร็จ');
      return result;
    } catch (err: any) {
      const message = err.response?.data?.error || 'Failed to update package';
      toast.error(message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const deletePackage = async (id: string, force = false, packageCode?: string) => {
    if (packageCode && isProtectedPackage(packageCode)) {
      toast.error('ไม่สามารถลบ Protected Package ได้');
      throw new Error('PROTECTED_PACKAGE');
    }
    try {
      setLoading(true);
      await packageAPI.delete(id, force);
      toast.success('ลบ package สำเร็จ');
    } catch (err: any) {
      const message = err.message === 'PROTECTED_PACKAGE'
        ? 'ไม่สามารถลบ Protected Package ได้'
        : err.response?.data?.error || 'Failed to delete package';
      toast.error(message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const toggleActive = async (id: string, isActive: boolean, packageCode?: string) => {
    if (packageCode && isProtectedPackage(packageCode) && !isActive) {
      toast.error('ไม่สามารถปิดใช้งาน Protected Package ได้');
      throw new Error('PROTECTED_PACKAGE');
    }
    try {
      setLoading(true);
      const result = await packageAPI.toggleActive(id, isActive);
      toast.success(`${isActive ? 'เปิดใช้งาน' : 'ปิดใช้งาน'} package สำเร็จ`);
      return result;
    } catch (err: any) {
      const message = err.message === 'PROTECTED_PACKAGE'
        ? 'ไม่สามารถปิดใช้งาน Protected Package ได้'
        : err.response?.data?.error || 'Failed to toggle package status';
      toast.error(message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return {
    createPackage,
    updatePackage,
    deletePackage,
    toggleActive,
    loading,
  };
}
